import { useState, useMemo, useEffect } from "react";
import {
  Calendar,
  Badge,
  Drawer,
  Alert,
  List,
  Avatar,
  Button,
  Modal,
  Form,
  Select,
  message,
} from "antd";
import { isWithinInterval } from "date-fns";

import { UserRoles } from "../../../features/user"

import { getFileUrl } from "../../../api/file";
import { getProjects } from "../../../api/project";
import { getTasks } from "../../../api/task";
import { assignTask } from "../../../api/dashboard";

export const filterProjectInfoFromDate = (date, projects) => {
  const projectForTheDay = projects.filter((p) =>
    isWithinInterval(date, {
      start: new Date(p.startAt),
      end: new Date(p.endAt),
    })
  );

  const activeEmployeeForTheDay = projectForTheDay.reduce((acc, p) => {
    const users = p.projectUsers
      .filter((pu) => pu.isActive)
      .map((pu) => pu.user);
    acc = [
      ...acc,
      ...users.filter(
        (u) =>
          u.role === UserRoles.Employee && !acc.find((accu) => accu.id === u.id)
      ),
    ];
    return acc;
  }, []);

  const activeEquipmentForTheDay = projectForTheDay.reduce((acc, p) => {
    const equipments = p.projectEquipments
      .filter((pu) => pu.isActive)
      .map((pe) => pe.equipment);

    acc = [
      ...acc,
      ...equipments.filter((eq) => !acc.find((acce) => acce.id === eq.id)),
    ];
    return acc;
  }, []);

  return {
    projectForTheDay,
    activeEmployeeForTheDay,
    activeEquipmentForTheDay,
  };
};

const getListData = (value, { projects, users, equipments }) => {
  const {
    activeEmployeeForTheDay,
    activeEquipmentForTheDay,
    projectForTheDay,
  } = filterProjectInfoFromDate(value.toDate(), projects);

  if (!projectForTheDay.length) return { listData: [] };

  const availableEmployees = users.filter(
    (u) => !activeEmployeeForTheDay.find((ae) => ae.id === u.id)
  );
  const availableEquipments = equipments.filter(
    (eq) => !activeEquipmentForTheDay.find((ae) => ae.id === eq.id)
  );

  return {
    availableEmployees,
    availableEquipments,
    projects: projectForTheDay,
    listData: [
      {
        type: "warning",
        dataType: "project",
        content: `${projectForTheDay.length} projects running`,
      },
      {
        type: "success",
        dataType: "user",
        content: `${availableEmployees.length} users available`,
      },
      {
        type: "success",
        dataType: "equipment",
        content: `${availableEquipments.length} equipments available`,
      },
    ],
  };
};

const getMonthData = (value) => {
  if (value.month() === 8) {
    return 1394;
  }
};

export default function MonthlyCalendar({
  equipments,
  users,
  projects,
  refresh,
  calendarValue,
}) {
  const summary = { equipments, users, projects };
  const [form] = Form.useForm();

  const [drawerType, setDrawerType] = useState("projects");
  const [drawerData, setDrawerData] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [searchProjects, setProjectOptions] = useState([]);
  const [taskOptions, setTaskOptions] = useState([]);

  const [project, setProject] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState(null);

  const populateSearch = async () => {
    const projects = await getProjects({ perPage: 5, page: 1 });
    setProjectOptions(projects.results);
  };

  const projectSearchOptions = useMemo(() => {
    return searchProjects.map((project) => {
      return {
        value: project.id,
        label: project.name,
      };
    });
  }, [searchProjects]);

  const updateTaskOptions = (project) => {
    if (!project) return;
    getTasks({ projectId: project, perPage: 5, page: 1 }).then((tasks) => {
      const taskOptions = tasks.results.map((task) => {
        return {
          value: task.id,
          label: task.name,
        };
      });
      setTaskOptions(taskOptions);
    });
  };

  const showDrawer = (drawerType, drawerData) => {
    setDrawerType(drawerType);
    setDrawerData(drawerData);
  };
  const onClose = () => {
    setDrawerData(null);
    setDrawerType(null);
  };

  const handleTaskSubmit = () => {
    form
      .validateFields()
      .then(async (values) => {
        await assignTask({
          ...values,
          equipmentId: selectedEquipment,
          userId: selectedEmployee,
        })
          .then(async () => {
            message.success("Task assigned successfully");
            form.resetFields();
            setSelectedEmployee(null);
            setSelectedEquipment(null);
            setProject(null);
            setShowTaskModal(false);
            await refresh();
          })
          .catch((err) => {
            message.error("Something went wrong!");
          });
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  const dateCellRender = (value) => {
    const { availableEmployees, availableEquipments, projects, listData } =
      getListData(value, summary);
    return (
      <div className="events overflow-x-hidden">
        {listData.map((item) => (
          <li
            key={item.content}
            onClick={() => {
              showDrawer(item.dataType, {
                availableEmployees,
                availableEquipments,
                projects,
              });
            }}
            className="list-none w-full "
          >
            <Badge
              status={item.type}
              text={item.content}
              className="text-xs font-normal leading-[22px] truncate w-full "
            />
          </li>
        ))}
      </div>
    );
  };

  useEffect(() => {
    populateSearch();
  }, []);

  useEffect(() => {
    if (project) {
      updateTaskOptions(project);
    }
  }, [project]);

  return (
    <div>
      <Calendar
        cellRender={summary ? dateCellRender : null}
        value={calendarValue}
        headerRender={() => (<div />)}
      />
      {!!drawerData && (
        <Drawer
          title={
            drawerType === "project"
              ? "Projects"
              : drawerType === "user"
                ? "Users"
                : "Equipments"
          }
          placement={"right"}
          closable={false}
          onClose={onClose}
          open={!!drawerData}
          key={"right"}
        >
          <List>
            <p>
              You have{" "}
              {drawerType === "project"
                ? drawerData.projects.length
                : drawerType === "user"
                  ? drawerData.availableEmployees.length
                  : drawerData.availableEquipments.length}{" "}
              {drawerType}
            </p>

            {drawerType === "project" &&
              drawerData.projects.map((project) => (
                <Alert
                  id={project.id}
                  type="info"
                  message={project.name}
                  description={project.description}
                  className="my-2"
                />
              ))}
            {drawerType === "user" &&
              drawerData.availableEmployees.map((user, index) => (
                <List.Item
                  key={index}
                  actions={[
                    <Button
                      type="link"
                      onClick={() => {
                        setSelectedEmployee(user.id);
                        setShowTaskModal(true);
                      }}
                    >
                      Assign Task
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        src={user.avatar ? getFileUrl(user.avatar) : null}
                      />
                    }
                    title={user.firstName}
                    description={user.role}
                  />
                </List.Item>
              ))}
            {drawerType === "equipment" &&
              drawerData.availableEquipments.map((equipment, index) => (
                <List.Item
                  actions={[
                    <Button
                      type="link"
                      onClick={() => {
                        setSelectedEquipment(equipment.id);
                        setShowTaskModal(true);
                      }}
                    >
                      Assign Task
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        src={
                          equipment?.avatars?.length > 0
                            ? getFileUrl(equipment.avatars[0])
                            : undefined
                        }
                      />
                    }
                    title={equipment.name}
                    description={equipment.nickName}
                  />
                </List.Item>
              ))}
          </List>
        </Drawer>
      )}
      <Modal
        open={showTaskModal}
        onCancel={() => setShowTaskModal(false)}
        title="Assign Task"
        onOk={() => form.submit()}
      >
        <Form form={form} onFinish={handleTaskSubmit} layout="vertical">
          <Form.Item
            name={"projectId"}
            label="Project"
            rules={[{ required: true }]}
          >
            <Select
              placeholder={"Select Project"}
              showSearch
              options={projectSearchOptions}
              onSearch={async (str) => {
                const { results: projects } = await getProjects({ q: str });
                setProjectOptions(projects);
              }}
              onChange={(e) => {
                setProject(e);
              }}
              rules={[{ required: true }]}
              defaultActiveFirstOption={false}
              filterOption={false}
              mode="tag"
            />
          </Form.Item>
          <Form.Item name={"taskId"} label="Task" >
            <Select
              disabled={!project}
              placeholder={"Select Task"}
              showSearch
              options={taskOptions}
              onSearch={async (str) => {
                const { results: tasks } = await getTasks({
                  q: str,
                  projectId: project,
                });
                setTaskOptions(tasks);
              }}
              defaultActiveFirstOption={false}
              filterOption={false}
              mode="tag"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
